import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { getServices } from "../api";
import { SERVICE_DETAILS, serviceSlug } from "../data/serviceDetails";
import { DetailHero, StatsStrip, FeatureBento } from "../components/detail/DetailTop";
import { HowItWorks, UseCases, ApiPreview, DetailFAQ, RelatedServices, DetailCTA } from "../components/detail/DetailSections";

const fallback = (s) => ({
  tagline: s.highlights?.[0]?.sub || "",
  stats: (s.chips || []).map((c) => ({ v: c.title, l: c.sub })),
  features: [...(s.highlights || []), ...(s.chips || [])].map((h) => ({ icon: "Sparkles", title: h.title, desc: h.sub })),
  steps: [{ t: "Sign up", d: "Create your account." }, { t: "Complete KYC", d: "Verify your business." }, { t: "Integrate", d: "Dashboard or API." }, { t: "Go live", d: "Start transacting." }],
  useCases: [], faqs: [],
});

export default function ServiceDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [services, setServices] = useState(null);

  useEffect(() => { window.scrollTo({ top: 0 }); }, [slug]);
  useEffect(() => { getServices().then(setServices).catch(() => setServices([])); }, []);

  if (!services) return <div className="min-h-screen flex items-center justify-center bg-[#050C17]"><Loader2 className="animate-spin text-[#06B6D4]" /></div>;

  const s = services.find((x) => serviceSlug(x) === slug);
  if (!s) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-[#050C17] text-white" data-testid="detail-not-found">
        <h1 className="text-3xl font-extrabold">Service not found</h1>
        <button onClick={() => navigate("/#services")} className="px-5 py-2.5 rounded-full bg-[#06B6D4] text-[#030712] font-semibold" data-testid="detail-back-services">Back to services</button>
      </div>
    );
  }
  const d = SERVICE_DETAILS[s.visual] || fallback(s);
  const sameGroup = services.filter((x) => x.id !== s.id && x.group === s.group);
  const related = [...sameGroup, ...services.filter((x) => x.id !== s.id && x.group !== s.group)].slice(0, 3);

  return (
    <div className="relative" data-testid="service-detail-page">
      <Header />
      <main>
        <DetailHero s={s} d={d} />
        {d.stats.length > 0 && <StatsStrip stats={d.stats} />}
        {d.features.length > 0 && <FeatureBento features={d.features} />}
        <HowItWorks steps={d.steps} />
        {d.useCases.length > 0 && <UseCases items={d.useCases} label={s.label} />}
        {d.api && <ApiPreview api={d.api} />}
        {d.faqs.length > 0 && <DetailFAQ faqs={d.faqs} />}
        <RelatedServices items={related} />
        <DetailCTA label={s.label} />
      </main>
      <Footer />
    </div>
  );
}
